import './style.css';
import '@/ui/@mobiscroll/react/dist/css/mobiscroll.scss';

import { set, subYears } from 'date-fns';
import { useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Dimensions, Pressable, ScrollView, View } from 'react-native';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Reservation } from '@/api';
import type { Car } from '@/api/cars/types';
import { useCalendarCars } from '@/api/partners/cars/use-calendar-cars';
import { useDashboardMetrics } from '@/api/partners/dashboard/use-dashboard-metrics';
import { useCalendarReservations } from '@/api/partners/reservations/use-calendar-reservations';
import type { Filter } from '@/api/partners/reservations/use-reservation-filters';
import { FilterName } from '@/api/partners/reservations/use-reservation-filters';
import { NoCarsEmptyState } from '@/components/first-car-empty-state';
import { useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useDebounce } from '@/core/hooks/use-debounced-value';
import {
  capitalizeFirstLetter,
  getBackgroundImageUri,
  titleCaseToSnake,
  titleize,
} from '@/core/utils';
import { GREY_30, GREY_70, PINKLING_10 } from '@/style/colors';
import { Button, Input, Text } from '@/ui';
import ButtonFilter from '@/ui/button-filter';
import { Calendar } from '@/ui/calendar';
import { Chip, ChipType } from '@/ui/chip';
import type {
  EventcalendarBase,
  MbscCalendarEvent,
  MbscEventcalendarView,
  MbscEventClickEvent,
  MbscEventCreatedEvent,
  MbscEventCreateFailedEvent,
  MbscPageLoadedEvent,
  MbscResource,
  MbscSelectedDateChangeEvent,
} from '@/ui/event-calendar';
import {
  CalendarNav,
  CalendarNext,
  CalendarPrev,
  CalendarToday,
  Eventcalendar,
} from '@/ui/event-calendar';
import { Icon } from '@/ui/icon';
import { Search } from '@/ui/icons';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { Dot } from '@/ui/icons/dot';
import { Loader } from '@/ui/loader';

import { BlockCarModal } from '../block-car-modal';
import { DeleteBlockModal } from '../delete-block-modal';
import {
  getPartnerReservationStatusColor,
  getPartnerReservationStatusLabel,
} from '../status-label';

const PAGE_HEIGHT = Dimensions.get('window').height;

export default function ReservationsCalendar() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const status = useAuth.use.status();
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDatetime, setStartDatetime] = useState<Date | null>(null);
  const [endDatetime, setEndDatetime] = useState<Date | null>(null);
  const [filterText, setFilterText] = useState('');
  const debouncedText = useDebounce(filterText, 400);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [tempFilters, setTempFilters] = useState<Filter[]>([]);
  const [activeFilterIdx, setActiveFilterIdx] = useState<number | null>(null);
  const [dateType, setDateType] = useState<'month' | 'week'>('month');
  const [selectedLocations, setSelectedLocations] = useState<number[] | null>(
    null
  );
  const [firstDay, setFirstDay] = useState<Date | null>(null);
  const [lastDay, setLastDay] = useState<Date | null>(null);
  const [showBlockCarModal, setShowBlockCarModal] = useState(false);
  const [showDeleteBlockModal, setShowDeleteBlockModal] = useState(false);
  const [activeTrip, setActiveTrip] = useState<Reservation | null>(null);
  const [newBlock, setNewBlock] = useState<{
    startDate: Date;
    endDate: Date;
    carId: string;
  } | null>(null);
  const [page, setPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const { data: dashboardMetricsData, isLoading: isLoadingDashboardMetrics } =
    useDashboardMetrics({});

  const {
    data: calendarTrips,
    isLoading: isLoadingCalendarReservations,
    refetch: refetchCalendarTrips,
  } = useCalendarReservations({
    enabled: !!firstDay && !!lastDay,
    variables: {
      startDatetime: firstDay?.toISOString(),
      endDatetime: lastDay?.toISOString(),
      cityIds: selectedLocations,
    },
  });

  const {
    data: calendarCars,
    isLoading: isLoadingCalendarCars,
    refetch: refetchCalendarCars,
  } = useCalendarCars({
    enabled: !!firstDay && !!lastDay,
  });

  useEffect(() => {
    if (status === 'signIn' && firstDay && lastDay) {
      refetchCalendarTrips();
    }
  }, [status]);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Calendar',
    });
  }, []);

  const newBlockCar = useMemo((): Car | undefined => {
    if (!calendarCars) return undefined;

    return calendarCars.find(
      (car: Car) => car.id === parseInt(newBlock?.carId, 10)
    );
  }, [newBlock]);

  const renderStatusChipType = (status: string): ChipType => {
    switch (status) {
      case 'open':
      case 'verified':
      case 'rental':
        return ChipType.SUCCESS;
      case 'billable':
      case 'cleaning':
        return ChipType.PENDING;
      case 'late':
        return ChipType.WARNING;
      default:
        return ChipType.DEFAULT;
    }
  };

  const view = useMemo((): MbscEventcalendarView => {
    return {
      timeline: {
        type: dateType,
        resolution: 'day',
        currentTimeIndicator: true,
      },
    };
  }, [dateType]);

  const getResourceId = (car: Car): string => {
    return `${car.defaultLocation?.city}__${car.id}`;
  };

  const resources = useMemo((): MbscResource[] => {
    if (!calendarCars) return [];

    const cityMap: { [key: string]: Car[] } = {};
    calendarCars.forEach((car: Car) => {
      const carCity = car.defaultLocation?.city;
      if (!cityMap[carCity]) {
        cityMap[carCity] = [car];
      } else if (cityMap[carCity].find((c) => c.id === car.id) === undefined) {
        cityMap[carCity].push(car);
      }
    });

    const resources = Object.keys(cityMap).map((city: string) => {
      const car = cityMap[city].find(
        (c) =>
          c.defaultLocation &&
          typeof c.defaultLocation.timezone === 'string' &&
          c.defaultLocation.timezone.length > 0
      );
      const carZoneShort = car
        ? DateTime.now().setZone(car.defaultLocation.timezone).offsetNameShort
        : '';

      return {
        id: `gro${city}`,
        name: `${city} (${carZoneShort})`,
        color: '#fdf500',
        eventCreation: false,
        children: cityMap[city]
          .filter(
            (car: Car) =>
              car.licensePlate
                ?.toLowerCase()
                .includes(filterText.toLowerCase()) ||
              car.model?.displayName
                .toLowerCase()
                .includes(filterText.toLowerCase()) ||
              car.vin?.toLowerCase().includes(filterText.toLowerCase())
          )
          .filter((car: Car) => {
            if (selectedLocations && selectedLocations.length > 0) {
              return selectedLocations.includes(car.defaultLocation.id);
            }
            return car;
          })
          .map((car: Car) => ({
            id: getResourceId(car),
            name: `${car.vehicleYear} ${car.brandName} ${car.model?.displayName}`,
            title: `${car.licensePlate} | ${car.vin}`,
            img_uri: getBackgroundImageUri(
              titleCaseToSnake(car.brandName),
              titleCaseToSnake(car.modelName)
            ),
            background: car.onSubscription ? PINKLING_10 : '',
          })),
      };
    });

    return resources.filter(
      (res: MbscResource) => res.children && res.children.length > 0
    );
  }, [calendarCars, calendarTrips, filterText, dateType, selectedLocations]);

  const events = useMemo((): MbscCalendarEvent[] => {
    if (!calendarTrips) return [];

    const userZone = DateTime.local().zoneName;

    return calendarTrips.map((res: Reservation) => {
      return {
        id: res.id,
        start: DateTime.fromISO(res.startTime, { setZone: true })
          .setZone(userZone, { keepLocalTime: true })
          .toISO(),
        end: DateTime.fromISO(res.endTime, { setZone: true })
          .setZone(userZone, { keepLocalTime: true })
          .toISO(),
        title: `${res.customerName} #${res.id}`,
        resource: getResourceId(res.car),
        color: getPartnerReservationStatusColor(res.status, res.isBlock),
        tooltip: `
          Current status: ${getPartnerReservationStatusLabel(
            res.status,
            res.isBlock
          )}\n
          Rented for ${res.numDays} days \n
          Pickup Location: ${
            res.customPickupLocation || res.pickupLocation.address
          }\n
          Dropoff Location: ${
            res.customReturnLocation || res.returnLocation.address
          }
        `,
      };
    });
  }, [calendarTrips, dateType]);
  const hasNoCars =
    !isLoadingDashboardMetrics &&
    (dashboardMetricsData?.totalCarsCount ?? 0) === 0;

  const handleCalendarLoad = (
    args: MbscPageLoadedEvent,
    inst: EventcalendarBase
  ) => {
    setFirstDay(args.firstDay);
    setLastDay(args.lastDay);
  };

  const handleEventCreate = (
    args: MbscEventCreatedEvent,
    inst: EventcalendarBase
  ) => {
    const resource = args.event.resource as string;
    if (!resource) return;

    const carId = resource.split('__')[1];

    if (carId) {
      setShowBlockCarModal(true);

      setNewBlock({
        startDate: new Date(args.event.start),
        endDate: new Date(args.event.end),
        carId,
      });
    }

    setTimeout(() => {
      inst.setEvents(events);
    }, 500);
  };

  const handleEventCreateFailed = (
    args: MbscEventCreateFailedEvent,
    inst: EventcalendarBase
  ) => {
    if (args.invalid) {
      Toast.show({
        type: 'error',
        text1: 'An error occured.',
      });
    } else if (args.overlap) {
      Toast.show({
        type: 'error',
        text1: 'This car is already rented for this date.',
      });
    }
  };

  const handleEventClick = (
    args: MbscEventClickEvent,
    inst: EventcalendarBase
  ) => {
    const resId = args.event.id;
    const trip = calendarTrips.find((res: Reservation) => res.id === resId);

    if (trip?.isBlock) {
      setActiveTrip(trip);
      setShowDeleteBlockModal(true);
      return;
    }

    window.open(`/partner/reservations/${args.event.id}`, '_blank');
  };

  const handleDateChange = (
    args: MbscSelectedDateChangeEvent,
    inst: EventcalendarBase
  ) => {
    const startDate: Date | undefined = inst._firstDay;
    const endDate: Date | undefined = inst._lastDay;
    if (!startDate || !endDate) return;

    set;
  };

  const renderResource = (resource: any) => {
    return (
      <Pressable
        onPress={() => {
          window.open(`/partner/cars/${resource.id.split('__')[1]}`, '_blank');
        }}
        disabled={!resource.img_uri}
        style={{
          gap: 4,
        }}
      >
        {resource.background ? (
          <Chip
            type={ChipType.BRAND}
            icon={<Dot />}
            text="On Subscription"
            iconWidth={8}
            iconHeight={8}
            style={{ width: 120, height: 20 }}
          />
        ) : null}

        {/* {resource.img_uri && (
          <Image
            source={resource.img_uri}
            contentFit="contain"
            style={{
              height: 40,
              width: 56,
            }}
          />
        )} */}

        <Text type="bodyRegular">{resource.name}</Text>
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          {resource.title}
        </Text>
      </Pressable>
    );
  };

  if (isLoadingCalendarReservations || isLoadingCalendarCars) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (hasNoCars) {
    return (
      <NoCarsEmptyState
        title="Add Your First Car"
        body="Your calendar will show bookings and blocked dates for all the cars you list."
        ctaText="Add Your First Car"
        redirectPath="partner/cars/setup?fromSf=1"
      />
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
        maxHeight: PAGE_HEIGHT,
      }}
    >
      <View style={styles.container}>
        <View style={styles.nav}>
          <Text type="title1">Calendar</Text>
        </View>

        <View style={{ gap: 24, flex: 1 }}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              zIndex: 1,
            }}
          >
            <View style={{ flex: 1, gap: 16, zIndex: 1 }}>
              <Input
                placeholder="Search for ID, VIN and license"
                value={filterText}
                onChangeText={(t: string) => setFilterText(t)}
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<Search />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
                containerStyle={styles.input}
              />

              <ScrollView
                style={{ flexGrow: 0 }}
                horizontal
                showsHorizontalScrollIndicator={false}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    gap: 8,
                  }}
                >
                  {filters && filters.length > 0
                    ? filters
                        .filter((f) => {
                          return f.name === FilterName.LOCATION;
                        })
                        .map((filter: Filter, idx) => {
                          const formattedName = titleize(filter.name);

                          if (filter.collapsed) {
                            return null;
                          }

                          return (
                            <ButtonFilter
                              key={filter.name}
                              name={
                                filter.selected.length > 0
                                  ? `${formattedName} (${filter.selected.length})`
                                  : formattedName
                              }
                              headerTitle={formattedName}
                              handlePress={(selectedOptions: number[]) => {
                                const updatedFilters = [
                                  ...filters.slice(0, idx),
                                  {
                                    ...filter,
                                    selected: selectedOptions,
                                  },
                                  ...filters.slice(idx + 1),
                                ];

                                setTempFilters(updatedFilters);
                              }}
                              options={filter.options}
                              onOpen={() => {
                                setActiveFilterIdx(idx);
                              }}
                              onClose={() => {
                                setTempFilters([]);
                                setActiveFilterIdx(null);
                              }}
                              onClearAll={() => {
                                let selected: any[] = [];

                                const updatedFilters = [
                                  ...filters.slice(0, idx),
                                  {
                                    ...filter,
                                    selected,
                                  },
                                  ...filters.slice(idx + 1),
                                ];

                                setFilters(updatedFilters);

                                const updatedTempFilters = [
                                  ...tempFilters.slice(0, idx),
                                  {
                                    ...filter,
                                    selected,
                                  },
                                  ...tempFilters.slice(idx + 1),
                                ];

                                setTempFilters(updatedTempFilters);

                                if (activeMode === 'calendar') {
                                  setSelectedLocations([]);
                                }
                              }}
                              onSave={(selectedOptions: number[]) => {
                                const updatedFilters = [
                                  ...filters.slice(0, idx),
                                  {
                                    ...filter,
                                    selected: selectedOptions,
                                  },
                                  ...filters.slice(idx + 1),
                                ];

                                setFilters(updatedFilters);
                                setActiveFilterIdx(null);

                                setSelectedLocations(selectedOptions);
                              }}
                              opened={activeFilterIdx === idx}
                              active={activeFilterIdx === idx}
                              selected={filter.selected}
                            />
                          );
                        })
                    : null}
                </View>
              </ScrollView>

              <View style={{ gap: 16, zIndex: 1 }}>
                <Calendar
                  mode="date"
                  isVisible={showCalendar}
                  headerTitle={'Date'}
                  minDate={subYears(new Date(), 2)}
                  defaultStartDate={startDatetime}
                  defaultEndDate={endDatetime}
                  onHide={() => {
                    setShowCalendar(false);
                  }}
                  onClear={() => {
                    setStartDatetime(null);
                    setEndDatetime(null);
                    setShowCalendar(false);
                  }}
                  onDatesSelected={(startDt, endDt) => {
                    setStartDatetime(startDt);
                    setEndDatetime(endDt);
                    setShowCalendar(false);
                  }}
                  range
                  style={{
                    zIndex: 1,
                    position: 'absolute',
                    top: -75,
                    left: 100,
                  }}
                  containerStyle={{
                    borderWidth: 1,
                    borderColor: theme.colors.card.default.borderColor,
                    shadowColor: theme.colors.card.default.borderColor,
                    shadowOffset: {
                      width: 0,
                      height: 5,
                    },
                    shadowOpacity: 0.5,
                    shadowRadius: 5,
                    elevation: 10,
                    height: '100%',
                  }}
                  id="reservations-calendar"
                  hasBackdrop
                  coverScreen
                  showDesktopOverview
                />
              </View>
            </View>
          </View>

          <ScrollView
            showsVerticalScrollIndicator={false}
            style={{
              borderColor:
                UnistylesRuntime.themeName === 'dark' ? GREY_70 : GREY_30,
              borderWidth: 1,
              borderRadius: 16,
            }}
            contentContainerStyle={{ height: '100%' }}
          >
            <Eventcalendar
              theme="ios"
              themeVariant={UnistylesRuntime.themeName}
              view={view}
              data={events}
              resources={resources}
              width="100%"
              dragToCreate
              onEventCreated={handleEventCreate}
              onEventCreateFailed={handleEventCreateFailed}
              onEventDoubleClick={handleEventClick}
              onSelectedDateChange={handleDateChange}
              onPageLoaded={handleCalendarLoad}
              eventOverlap={false}
              renderResource={renderResource}
              renderHeader={() => (
                <>
                  <View>
                    <View style={{ flexDirection: 'row' }}>
                      <CalendarNav className="mds-header-filter-nav" />

                      <Button
                        type="secondaryExtraSmall"
                        text={capitalizeFirstLetter(dateType)}
                        onPress={() => {
                          setDateType(dateType === 'month' ? 'week' : 'month');
                        }}
                        style={{ width: 80, height: 40 }}
                        rightIcon={
                          <Icon
                            type="naked"
                            icon={<ChevronRight />}
                            fillColor={theme.colors.icon.color}
                            width={12}
                            height={12}
                            iconStyle={{
                              transform: [{ rotate: '90deg' }],
                            }}
                          />
                        }
                      />
                    </View>
                  </View>
                  <div className="mds-header-filter mbsc-flex-1-0" />
                  <CalendarPrev
                    className={
                      UnistylesRuntime.themeName === 'dark'
                        ? 'mds-header-filter-prev-dark'
                        : 'mds-header-filter-prev'
                    }
                  />
                  <CalendarToday
                    className={
                      UnistylesRuntime.themeName === 'dark'
                        ? 'mds-header-filter-today-dark'
                        : 'mds-header-filter-today'
                    }
                  />
                  <CalendarNext
                    className={
                      UnistylesRuntime.themeName === 'dark'
                        ? 'mds-header-filter-next-dark'
                        : 'mds-header-filter-next'
                    }
                  />
                </>
              )}
            />
          </ScrollView>
        </View>
      </View>

      {newBlock && newBlockCar ? (
        <BlockCarModal
          visible={showBlockCarModal}
          startDate={newBlock.startDate}
          endDate={newBlock.endDate}
          car={newBlockCar}
          onClose={() => {
            setShowBlockCarModal(false);
          }}
          onSuccess={() => {
            Toast.show({
              text1: 'Car blocked successfully',
              type: 'success',
            });
            setShowBlockCarModal(false);
            refetchCalendarTrips();
          }}
        />
      ) : null}

      <DeleteBlockModal
        visible={showDeleteBlockModal}
        trip={activeTrip}
        onClose={() => {
          setShowDeleteBlockModal(false);
        }}
        onSuccess={() => {
          Toast.show({
            text1: 'Block deleted successfully',
            type: 'success',
          });
          setShowDeleteBlockModal(false);
          refetchCalendarTrips();
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    borderRadius: 16,
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
    maxHeight: '100%',
    gap: 56,
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  input: {
    width: {
      md: 400,
    },
  },
  reservationsContainer: {
    flex: {
      xs: 1,
      md: undefined,
    },
  },
}));
